import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import CountryPicker from "@/v2-backoffice-client/country/CountryPicker"
import ProjectOption from "@/v2-backoffice-client/project/ProjectOption"
import { NO_PROJECT_VALUE } from "@/v2-backoffice-client/project/project.constants"
import type { UserActiveContext } from "@/v2-backoffice-client/user/update/user.update.hooks"

export type UserActiveContextFieldsProps = {
  // The three fields are one coupled state machine (team scopes the project
  // list, project settles the country), so they arrive as the one object
  // useUserActiveContext returns rather than a dozen loose props.
  context: UserActiveContext
}

// The active team / project / country pickers of the user update dialog.
// Pure presentation: every value and rule it renders is derived in
// useUserActiveContext, which the submitting form also reads.
function UserActiveContextFields(props: UserActiveContextFieldsProps) {
  const { context } = props

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="editActiveTeam">Active Team</Label>
        <Select
          value={
            context.tenantId != null ? String(context.tenantId) : undefined
          }
          onValueChange={context.selectTenant}
        >
          <Select.Trigger
            id="editActiveTeam"
            size="md"
            className="w-full"
            title="The team the user is currently active in. Applies from their next session refresh."
          >
            <Select.Value placeholder="No active team" />
          </Select.Trigger>
          <Select.Content>
            {context.userTenants.map((userTenant) => (
              <Select.Item
                key={userTenant.Id}
                value={String(userTenant.TenantId)}
              >
                {userTenant.Tenant?.Name ?? `#${userTenant.TenantId}`}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>

      {context.tenantId != null && (
        <div className="space-y-2">
          <Label htmlFor="editActiveProject">Active Project</Label>
          <Select
            value={context.effectiveProjectId ?? NO_PROJECT_VALUE}
            onValueChange={(value) => {
              context.setProjectId(value === NO_PROJECT_VALUE ? null : value)
            }}
          >
            <Select.Trigger
              id="editActiveProject"
              size="md"
              className="w-full"
              title="The project the user lands in. Leave as 'No project' to let them pick from the project list."
            >
              <Select.Value
                placeholder={
                  context.projectsLoading ? "Loading projects..." : "No project"
                }
              />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value={NO_PROJECT_VALUE}>No project</Select.Item>
              {context.projects.map((project) => (
                <Select.Item key={project.Id} value={project.Id}>
                  <ProjectOption
                    name={project.Name}
                    countryCode={project.CountryCode}
                    createdAt={project.CreatedAt}
                  />
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
          {context.staleProject && (
            <p className="text-xs text-muted-foreground">
              The project this user was in is gone from this team. Saving clears
              it.
            </p>
          )}
        </div>
      )}

      {context.tenantId != null && (
        <div className="space-y-2">
          <Label htmlFor="editActiveCountry">Active Country</Label>
          <CountryPicker
            className="w-full"
            value={context.effectiveCountryCode}
            onValueChange={context.setCountryCode}
            disabled={context.countryFollowsProject}
            emptyLabel="Not set"
            placeholder="Not set"
            title="The country the user's data is scoped to. Applies from their next session refresh."
          />
          <p className="text-xs text-muted-foreground">
            {context.countryFollowsProject
              ? "Follows the active project — change the project to change the country."
              : "Set directly, because this user has no active project to take a country from."}
          </p>
        </div>
      )}
    </>
  )
}

export default UserActiveContextFields
